import { Combobox as ComboboxPrimitive } from '@base-ui/react'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

// IMPORTANT: Base UI's Combobox does NOT automatically hide items when filtering.
// Items rendered as React children are always visible in the DOM. To implement search/filter:
// 1. Pass `filter={null}` to Combobox root to disable internal filtering
// 2. Use `onInputValueChange` to capture the search query in local state
// 3. Filter your items array before rendering and conditionally show an empty state
// See MultiSelectField for a working example.
const Combobox = ComboboxPrimitive.Root

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger data-slot="combobox-trigger" className={className} {...props}>
      {children}
    </ComboboxPrimitive.Trigger>
  )
}

function ComboboxInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <div
      className={cn(
        'tw:flex tw:items-center tw:border-b tw:border-gray-200 tw:focus-within:border-primary-400 tw:px-3 tw:transition-colors',
        className
      )}
    >
      <Icon type="search-lg" width={16} height={16} className="tw:mr-2 tw:shrink-0 tw:text-gray-400" />
      <ComboboxPrimitive.Input
        data-slot="combobox-input"
        className="tw:flex tw:h-10 tw:w-full tw:bg-transparent tw:py-3 tw:text-base tw:outline-hidden tw:placeholder:text-gray-400 tw:disabled:cursor-not-allowed tw:disabled:opacity-50"
        {...props}
      />
    </div>
  )
}

function ComboboxContent({
  className,
  portalContainer,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  collisionAvoidance,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor' | 'collisionAvoidance'
  > & { portalContainer?: ComboboxPrimitive.Portal.Props['container'] }) {
  return (
    <ComboboxPrimitive.Portal container={portalContainer}>
      <div className="tailwind">
        <ComboboxPrimitive.Positioner
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          anchor={anchor}
          collisionAvoidance={collisionAvoidance}
          className="tw:pointer-events-auto tw:z-50"
          style={{ pointerEvents: 'auto' }}
        >
          <ComboboxPrimitive.Popup
            data-slot="combobox-content"
            className={cn(
              'tw:max-h-(--available-height) tw:w-(--anchor-width) tw:min-w-[220px] tw:origin-(--transform-origin)',
              'tw:overflow-hidden tw:rounded-md tw:bg-white tw:text-gray-700',
              'tw:shadow-[0_0_0_1px_#e5e7eb,0_4px_6px_-1px_rgba(0,0,0,0.1)]',
              'tw:animate-in tw:fade-in-0 tw:zoom-in-95',
              className
            )}
            {...props}
          />
        </ComboboxPrimitive.Positioner>
      </div>
    </ComboboxPrimitive.Portal>
  )
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn('tw:max-h-[300px] tw:overflow-y-auto tw:overscroll-contain tw:p-1', className)}
      {...props}
    />
  )
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        'tw:relative tw:flex tw:w-full tw:cursor-pointer tw:select-none tw:items-center tw:gap-2 tw:rounded-xs tw:px-3 tw:py-2 tw:text-base tw:outline-hidden tw:transition-colors',
        'tw:data-highlighted:bg-primary-100 tw:data-highlighted:text-primary-900',
        'tw:data-disabled:pointer-events-none tw:data-disabled:opacity-50',
        className
      )}
      {...props}
    >
      {children}
    </ComboboxPrimitive.Item>
  )
}

// A ComboboxItem with a checkbox indicator, for multi-select popovers. Selection is
// announced by Base UI via the item's aria-selected; the box is decorative (aria-hidden).
function ComboboxCheckItem({
  selected,
  className,
  indicatorClassName,
  children,
  ...props
}: ComboboxPrimitive.Item.Props & { selected: boolean; indicatorClassName?: string }) {
  return (
    <ComboboxItem className={className} {...props}>
      <span
        aria-hidden
        className={cn(
          'tw:flex tw:h-4 tw:w-4 tw:shrink-0 tw:items-center tw:justify-center tw:rounded tw:border tw:transition-colors',
          selected ? 'tw:border-primary-500 tw:bg-primary-50' : 'tw:border-gray-300 tw:bg-white',
          indicatorClassName
        )}
      >
        {selected && <Icon type="check" width={12} height={12} className="tw:text-primary-500 tw:stroke-2" />}
      </span>
      {children}
    </ComboboxItem>
  )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn('tw:overflow-hidden tw:text-gray-700', className)}
      {...props}
    />
  )
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn('tw:px-3 tw:py-1.5 tw:text-sm tw:font-semibold', className)}
      {...props}
    />
  )
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn('tw:py-6 tw:text-center tw:text-sm tw:text-gray-400', className)}
      {...props}
    />
  )
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn('tw:-mx-1 tw:my-1 tw:h-px tw:bg-gray-200', className)}
      {...props}
    />
  )
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxCheckItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
}
